import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Box, Paper, Typography, Grid, Button, Select, MenuItem, TextField, Table, TableHead, TableRow, TableCell, TableBody, IconButton, Divider, TableContainer } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import expenseService from '../../services/expenseService';
import { useSnackbar } from 'notistack';
import { format } from 'date-fns';
import { styled } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';

const DEFAULT_REASONS = [
  { value: 'SALARY', label: 'Salary' },
  { value: 'RENT', label: 'Rent' },
  { value: 'FOOD_ALLOWANCE', label: 'Food Allowance' },
  { value: 'TRAVEL_ALLOWANCE', label: 'Travel Allowance' },
  { value: 'UTILITIES', label: 'Utilities' },
  { value: 'SUPPLIES', label: 'Supplies' },
  { value: 'MAINTENANCE', label: 'Maintenance' },
  { value: 'OTHER', label: 'Other' }
];

const emptyRow = () => ({ reason: 'OTHER', description: '', amount: '' });

const StyledTableHeadCell = styled(TableCell)(({ theme }) => ({
  padding: '10px 12px',
  border: '1px solid #bdc3c7',
  backgroundColor: '#154c79',
  color: 'white',
  fontWeight: 'bold',
  fontSize: '15px',
  textAlign: 'center',
  whiteSpace: 'nowrap',
  lineHeight: 1.2,
}));

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  padding: '8px 12px',
  border: '1px solid #e0e0e0',
  fontSize: '15px',
}));

const FunctionKey = styled(Button)(({ theme }) => ({
  minWidth: '100px',
  padding: '8px 12px',
  backgroundColor: '#ecf0f1',
  color: '#2c3e50',
  fontSize: '13px',
  fontWeight: '600',
  textTransform: 'none',
  border: '1px solid #bdc3c7',
  '&:hover': {
    backgroundColor: '#bdc3c7',
  },
  '& .key': {
    fontWeight: 'bold',
    color: '#3498db',
    fontSize: '14px',
    marginRight: '4px',
  },
}));

const ExpenseEntryPage = () => {
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const [entryDate, setEntryDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [rows, setRows] = useState([emptyRow()]);
  const [saving, setSaving] = useState(false);
  const reasonRefs = useRef([]);
  const descRefs = useRef([]);
  const amountRefs = useRef([]);
  const [reasonDropdownOpen, setReasonDropdownOpen] = useState({});

  const clearAll = useCallback(() => {
    setRows([emptyRow()]);
    setTimeout(() => {
      reasonRefs.current[0]?.focus();
    }, 0);
  }, []);

  const handleSave = useCallback(async () => {
    // Read all values from state to ensure we have the latest data
    const currentRows = rows.map((r) => {
      const amt = parseFloat(r.amount);
      return {
        reason: r.reason || 'OTHER', // Ensure reason is always set, default to OTHER if missing
        description: r.description || '',
        amount: isNaN(amt) ? 0 : Number(amt.toFixed(2)),
      };
    });

    // Filter out rows with zero amount
    const items = currentRows.filter(r => r.amount > 0);

    if (items.length === 0) {
      enqueueSnackbar('Add at least one expense item', { variant: 'warning' });
      return;
    }

    // Validate that all items have a valid reason
    const invalidItems = items.filter(r => !r.reason || r.reason === '');
    if (invalidItems.length > 0) {
      enqueueSnackbar('Please select a reason for all expense items', { variant: 'warning' });
      return;
    }

    const payload = {
      entryDate: new Date(entryDate),
      items
    };

    try {
      setSaving(true);
      await expenseService.createExpense(payload);
      enqueueSnackbar('Expense entry saved successfully!', { variant: 'success' });
      setRows([emptyRow()]);
      setTimeout(() => {
        reasonRefs.current[0]?.focus();
      }, 0);
    } catch (e) {
      enqueueSnackbar(e.response?.data?.message || 'Failed to save expenses', { variant: 'error' });
    } finally {
      setSaving(false);
    }
  }, [rows, entryDate, enqueueSnackbar]);

  useEffect(() => {
    const handler = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 's') {
        e.preventDefault();
        handleSave();
      } else if (e.key === 'F7') {
        e.preventDefault();
        clearAll();
      } else if (e.key === 'F3') {
        e.preventDefault();
        // Focus first description input
        descRefs.current[0]?.focus();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [handleSave, clearAll]);

  // Focus first reason on load
  useEffect(() => {
    setTimeout(() => {
      reasonRefs.current[0]?.focus();
    }, 0);
  }, []);

  const setRowValue = (index, key, value) => {
    setRows(prevRows => {
      const next = prevRows.slice();
      next[index] = { ...next[index], [key]: value };
      return next;
    });
  };

  const removeRow = (index) => {
    setRows(prevRows => {
      const next = prevRows.slice();
      next.splice(index, 1);
      if (next.length === 0) next.push(emptyRow());
      return next;
    });
  };

  const totalAmount = rows.reduce((sum, r) => {
    const v = parseFloat(r.amount);
    return sum + (isNaN(v) ? 0 : v);
  }, 0);

  const addNewRow = () => {
    setRows(prev => [...prev, emptyRow()]);
    setTimeout(() => {
      const idx = rows.length;
      reasonRefs.current[idx]?.focus();
    }, 0);
  };

  return (
    <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column', backgroundColor: '#f1f1f1', fontFamily: '"Roboto", sans-serif', overflow: 'hidden' }}>

      {/* 1. Header Section */}
      <Paper
        elevation={0}
        sx={{
          px: 2,
          py: 1,
          backgroundColor: '#154c79',
          color: 'white',
          borderRadius: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          zIndex: 1100,
        }}
      >
        <Typography variant="h5" sx={{ fontWeight: 'bold', letterSpacing: 1 }}>
          EXPENSE ENTRY
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="contained"
              size="small"
              onClick={() => navigate('/dashboard')}
              sx={{ backgroundColor: 'white', color: '#154c79', textTransform: 'none', height: 28, '&:hover': { backgroundColor: '#f5f5f5' } }}
              startIcon={<span style={{ fontSize: '1.2em', lineHeight: 0 }}>←</span>}
            >
              Back
            </Button>
            <Button
              variant="outlined"
              size="small"
              onClick={clearAll}
              sx={{ borderColor: '#ef5350', color: '#ef5350', backgroundColor: 'white', textTransform: 'none', height: 28, '&:hover': { backgroundColor: '#ffebee', borderColor: '#ef5350' } }}
              startIcon={<span style={{ fontSize: '1.2em', lineHeight: 0 }}>⟲</span>}
            >
              Clear
            </Button>
          </Box>
        </Box>
      </Paper>

      {/* 2. Top Info Section */}
      <Paper elevation={1} sx={{ p: 1.5, m: 1, borderRadius: 1 }}>
        <Grid container spacing={2} alignItems="center">
          {/* Left Side: Inputs */}
          <Grid item xs={12} md={9}>
            <Grid container spacing={2} alignItems="flex-end">
              <Grid item xs={12} md={3}>
                <Typography variant="caption" sx={{ fontWeight: 'bold', color: '#555', display: 'block', mb: 0.5 }}>Date</Typography>
                <TextField
                  type="date"
                  fullWidth
                  value={entryDate}
                  onChange={(e) => setEntryDate(e.target.value)}
                  size="small"
                  sx={{ backgroundColor: 'white', '& .MuiInputBase-input': { fontSize: '15px' } }}
                  inputProps={{ max: format(new Date(), 'yyyy-MM-dd') }}
                />
              </Grid>
            </Grid>
          </Grid>
          {/* Right Side: Big Total */}
          <Grid item xs={12} md={3} sx={{ textAlign: 'right' }}>
            <Typography variant="h2" sx={{ fontWeight: 'bold', color: '#e74c3c', lineHeight: 1, fontSize: '3.5rem' }}>
              {totalAmount.toFixed(2)}
            </Typography>
            <Typography variant="caption" sx={{ fontWeight: 'bold', color: '#7f8c8d' }}>NET AMOUNT</Typography>
          </Grid>
        </Grid>
      </Paper>
      {/* 3. Main Table Section */}
      <Box sx={{ flexGrow: 1, px: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
        <TableContainer component={Paper} elevation={1} sx={{ flexGrow: 1, borderRadius: 1 }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <StyledTableHeadCell sx={{ width: '60px' }}>S.No</StyledTableHeadCell>
                <StyledTableHeadCell sx={{ width: '220px' }}>Category</StyledTableHeadCell>
                <StyledTableHeadCell>Description</StyledTableHeadCell>
                <StyledTableHeadCell sx={{ width: '180px' }}>Amount</StyledTableHeadCell>
                <StyledTableHeadCell sx={{ width: '60px' }}>Action</StyledTableHeadCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row, idx) => {
                const isSearching = !row.amount || parseFloat(row.amount) === 0;
                const rowBg = isSearching ? '#fffde7' : idx % 2 === 0 ? '#f8f9fb' : 'white';
                return (
                  <TableRow key={idx} sx={{ backgroundColor: rowBg }}>
                    <StyledTableCell align="center" sx={{ fontWeight: 'bold' }}>
                      {idx + 1}
                    </StyledTableCell>
                    <StyledTableCell>
                      <Select
                        inputRef={(el) => (reasonRefs.current[idx] = el)}
                        value={row.reason || 'OTHER'}
                        onChange={(e) => {
                          setRowValue(idx, 'reason', e.target.value);
                          setReasonDropdownOpen(prev => ({ ...prev, [idx]: false }));
                          setTimeout(() => descRefs.current[idx]?.focus(), 0);
                        }}
                        fullWidth
                        size="small"
                        variant="standard"
                        disableUnderline
                        sx={{ fontSize: '15px' }}
                        open={!!reasonDropdownOpen[idx]}
                        onOpen={() => setReasonDropdownOpen(prev => ({ ...prev, [idx]: true }))}
                        onClose={() => setReasonDropdownOpen(prev => ({ ...prev, [idx]: false }))}
                        onKeyDown={(e) => {
                          const isOpen = !!reasonDropdownOpen[idx];
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            if (!isOpen) {
                              setReasonDropdownOpen(prev => ({ ...prev, [idx]: true }));
                            } else {
                              setReasonDropdownOpen(prev => ({ ...prev, [idx]: false }));
                              descRefs.current[idx]?.focus();
                            }
                          } else if (!isOpen && e.key === 'ArrowDown' && idx < rows.length - 1) {
                            e.preventDefault();
                            reasonRefs.current[idx + 1]?.focus();
                          } else if (!isOpen && e.key === 'ArrowUp' && idx > 0) {
                            e.preventDefault();
                            reasonRefs.current[idx - 1]?.focus();
                          }
                        }}
                      >
                        {DEFAULT_REASONS.map(r => (
                          <MenuItem key={r.value} value={r.value}>{r.label}</MenuItem>
                        ))}
                      </Select>
                    </StyledTableCell>
                    <StyledTableCell>
                      <TextField
                        inputRef={(el) => (descRefs.current[idx] = el)}
                        value={row.description}
                        onChange={(e) => setRowValue(idx, 'description', e.target.value)}
                        placeholder="Reason / description"
                        fullWidth
                        size="small"
                        variant="standard"
                        InputProps={{ disableUnderline: true, sx: { fontSize: '15px' } }}
                        onKeyDown={(e) => {
                          if (e.key === 'ArrowDown' && idx < rows.length - 1) {
                            e.preventDefault();
                            descRefs.current[idx + 1]?.focus();
                          } else if (e.key === 'ArrowUp' && idx > 0) {
                            e.preventDefault();
                            descRefs.current[idx - 1]?.focus();
                          } else if (e.key === 'Enter') {
                            e.preventDefault();
                            amountRefs.current[idx]?.focus();
                          }
                        }}
                      />
                    </StyledTableCell>
                    <StyledTableCell align="right">
                      <TextField
                        inputRef={(el) => (amountRefs.current[idx] = el)}
                        value={row.amount}
                        onChange={(e) => {
                          const val = e.target.value;
                          // Allow only numbers with optional decimal point
                          if (val === '' || /^[0-9]*\.?[0-9]*$/.test(val)) {
                            setRowValue(idx, 'amount', val);
                          }
                        }}
                        placeholder="0.00"
                        fullWidth
                        size="small"
                        variant="standard"
                        InputProps={{
                          disableUnderline: true,
                          inputProps: {
                            inputMode: 'decimal',
                            style: { textAlign: 'right', fontSize: '15px', fontWeight: 'bold' }
                          }
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            if (idx === rows.length - 1) {
                              addNewRow();
                            } else {
                              reasonRefs.current[idx + 1]?.focus();
                            }
                          } else if (e.key === 'ArrowDown' && idx < rows.length - 1) {
                            e.preventDefault();
                            amountRefs.current[idx + 1]?.focus();
                          } else if (e.key === 'ArrowUp' && idx > 0) {
                            e.preventDefault();
                            amountRefs.current[idx - 1]?.focus();
                          }
                        }}
                      />
                    </StyledTableCell>
                    <StyledTableCell align="center">
                      <IconButton size="small" color="error" onClick={() => removeRow(idx)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </StyledTableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      {/* 4. Footer Section */}
      <Paper elevation={2} sx={{ p: 1, m: 1, mt: 0, borderRadius: 1 }}>
        <Grid container spacing={2} justifyContent="flex-end" alignItems="center">
          <Grid item xs={12} md={3}>
            <Button
              fullWidth
              variant="contained"
              onClick={handleSave}
              disabled={saving}
              sx={{
                backgroundColor: '#27ae60 !important',
                color: 'white !important',
                opacity: 1,
                fontWeight: 'bold',
                fontSize: '18px',
                textTransform: 'none',
                py: 1.5,
                '&:hover': { backgroundColor: '#219150 !important' },
                '&.Mui-disabled': { backgroundColor: '#27ae60 !important', color: 'white !important', opacity: 0.7 }
              }}
              startIcon={<span style={{ fontSize: '24px', marginRight: '8px' }}>💾</span>}
            >
              {saving ? 'Saving...' : 'Save Entry'}
            </Button>
          </Grid>
        </Grid>
      </Paper>

    </Box >
  );
};

export default ExpenseEntryPage;
